import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import TrialBanner from "@/components/TrialBanner";
import {
  Clock,
  ChartBar,
  ListChecks,
  Buildings,
  Users,
  Gear,
  Plugs,
  SignOut,
  CreditCard,
  Envelope,
  Trash,
  List,
  X,
  Question,
  Car,
  CaretDown,
  Wrench,
  Receipt,
  ShieldCheck,
  CalendarCheck,
} from "@phosphor-icons/react";
import HelpModal from "@/components/HelpModal";

const NavItem = ({ to, icon: Icon, label, testId, onClick, badge, showDot }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={testId}
    onClick={onClick}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
        isActive
          ? "bg-sidebar-active text-white"
          : "text-sidebar-text hover:bg-sidebar-active/60"
      }`
    }
  >
    <Icon size={20} weight="regular" />
    <span className="flex-1">{label}</span>
    {badge != null && badge > 0 && (
      <span
        data-testid="trash-nav-badge"
        className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[11px] font-semibold"
      >
        {badge > 99 ? "99+" : badge}
      </span>
    )}
    {showDot && (
      <span
        data-testid={`${testId}-new-dot`}
        title="Nyheter"
        className="ml-auto w-2.5 h-2.5 rounded-full bg-[var(--brand-rust)] ring-2 ring-white/20 animate-pulse"
      />
    )}
  </NavLink>
);

const NAV_GROUP_STORAGE_KEY = "sidebar-admin-group-open";

const NavGroup = ({ label, icon: Icon, testId, defaultOpen, activeChild, collapsedBadge, children }) => {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(NAV_GROUP_STORAGE_KEY);
      if (saved === "true") return true;
      if (saved === "false") return false;
    } catch {
      /* ignore */
    }
    return !!defaultOpen;
  });

  // Om användaren navigerar till en sida som ligger i gruppen — expandera automatiskt
  useEffect(() => {
    if (activeChild && !open) setOpen(true);
  }, [activeChild, open]);

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(NAV_GROUP_STORAGE_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        data-testid={testId}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-sidebar-text hover:bg-sidebar-active/60 transition-colors"
      >
        <Icon size={20} weight="regular" />
        <span className="flex-1 text-left">{label}</span>
        {!open && collapsedBadge != null && collapsedBadge > 0 && (
          <span
            data-testid={`${testId}-collapsed-badge`}
            className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[11px] font-semibold"
          >
            {collapsedBadge > 99 ? "99+" : collapsedBadge}
          </span>
        )}
        <CaretDown
          size={14}
          weight="bold"
          className={`transition-transform duration-200 ${open ? "rotate-0" : "-rotate-90"}`}
        />
      </button>
      {open && (
        <div className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-1">
          {children}
        </div>
      )}
    </div>
  );
};

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const { shortName, companyName, logoUrl } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const isSuperAdmin = !!user?.is_super_admin;
  const [trashCount, setTrashCount] = useState(0);
  const [changelogUnread, setChangelogUnread] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!isAdmin) return undefined;
    const load = async () => {
      try {
        const api = (await import("@/lib/api")).default;
        const { data } = await api.get("/trash");
        if (!cancelled) setTrashCount(data?.total || 0);
      } catch {
        /* silent */
      }
    };
    load();
    // Uppdatera var 60:e sek + vid navigering (via key på location.pathname)
    const iv = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [isAdmin, location.pathname]);

  // Versionslogg: hämta antal olästa versioner + engångs-toast vid inloggning
  useEffect(() => {
    let cancelled = false;
    if (!isAdmin) return undefined;
    (async () => {
      try {
        const api = (await import("@/lib/api")).default;
        const { data } = await api.get("/changelog/status");
        if (cancelled) return;
        setChangelogUnread(data?.unread_count || 0);

        // Visa engångs-toast per session om det finns nya versioner sen förra besöket.
        // Vi undviker att spamma vid varje route-byte via sessionStorage.
        const shown = sessionStorage.getItem("changelog_toast_shown");
        if (!shown && data?.unread_count > 0 && data?.last_read_version) {
          sessionStorage.setItem("changelog_toast_shown", "1");
          // Import toast dynamically to avoid circular deps at module load
          const { toast } = await import("sonner");
          toast(
            `${data.unread_count} ny${data.unread_count === 1 ? "" : "a"} version${data.unread_count === 1 ? "" : "er"} sen ditt senaste besök`,
            {
              description: `Nu senast: v${data.latest_version}. Se vad som är nytt.`,
              action: {
                label: "Öppna",
                onClick: () => navigate("/versionslogg"),
              },
              duration: 8000,
            },
          );
        }
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, navigate]);

  // När admin besöker versionsloggen: nolla räknaren i UI direkt
  useEffect(() => {
    if (location.pathname === "/versionslogg" && changelogUnread > 0) {
      setChangelogUnread(0);
    }
  }, [location.pathname, changelogUnread]);

  const goHome = () => {
    navigate("/");
    onNavigate?.();
  };

  return (
    <>
      <button
        onClick={goHome}
        className="flex items-center gap-2 mb-6 text-left"
        data-testid="sidebar-logo"
      >
        {logoUrl ? (
          <div className="w-9 h-9 rounded-md bg-white/95 flex items-center justify-center overflow-hidden p-1">
            <img
              src={logoUrl}
              alt={companyName}
              className="max-w-full max-h-full object-contain"
              data-testid="sidebar-logo-image"
            />
          </div>
        ) : (
          <div className="w-9 h-9 rounded-md bg-brand flex items-center justify-center font-heading text-white text-lg">
            {(companyName[0] || "T").toUpperCase()}
          </div>
        )}
        <div>
          <div className="font-heading text-base text-white">Tidsrapporten</div>
          <div
            className="text-[10px] uppercase tracking-widest text-sidebar-text/70 truncate max-w-[10rem]"
            data-testid="sidebar-short-name"
            title={companyName}
          >
            {shortName}
          </div>
        </div>
      </button>

      <nav className="flex-1 min-h-0 space-y-1 overflow-y-auto">
        <NavItem to="/" icon={Clock} label="Stämpelklocka" testId="nav-stamp" onClick={onNavigate} />
        <NavItem to="/dashboard" icon={ChartBar} label="Dashboard" testId="nav-dashboard" onClick={onNavigate} />
        {!isAdmin && (
          <NavItem to="/mina-timmar" icon={CalendarCheck} label="Mina timmar" testId="nav-my-month" onClick={onNavigate} />
        )}
        {isAdmin && (
          <NavItem to="/reports" icon={ListChecks} label="Rapporter" testId="nav-reports" onClick={onNavigate} />
        )}
        <NavItem to="/objects" icon={Buildings} label="Objekt" testId="nav-objects" onClick={onNavigate} />
        <NavItem to="/korjournal" icon={Car} label="Körjournal" testId="nav-drive-log" onClick={onNavigate} />
        {isAdmin && (
          <NavGroup
            label="Administrera"
            icon={Wrench}
            testId="nav-admin-group"
            defaultOpen={false}
            collapsedBadge={trashCount}
            activeChild={[
              "/tasks",
              "/employees",
              "/integrations",
              "/billing",
              "/papperskorg",
            ].some((p) => location.pathname.startsWith(p))}
          >
            <NavItem to="/tasks" icon={ListChecks} label="Moment" testId="nav-tasks" onClick={onNavigate} />
            <NavItem to="/employees" icon={Users} label="Anställda" testId="nav-employees" onClick={onNavigate} />
            <NavItem to="/loneinstallningar" icon={Clock} label="Löneinställningar" testId="nav-workday-admin" onClick={onNavigate} />
            <NavItem to="/attest" icon={ShieldCheck} label="Attest" testId="nav-attest" onClick={onNavigate} />
            <NavItem to="/lonerapport" icon={Receipt} label="Lönerapport" testId="nav-payroll-report" onClick={onNavigate} />
            <NavItem to="/integrations" icon={Plugs} label="Integrationer" testId="nav-integrations" onClick={onNavigate} />
            <NavItem to="/billing" icon={CreditCard} label="Fakturering" testId="nav-billing" onClick={onNavigate} />
            <NavItem
              to="/papperskorg"
              icon={Trash}
              label="Papperskorg"
              testId="trash-nav-link"
              onClick={onNavigate}
              badge={trashCount}
            />
          </NavGroup>
        )}
        <NavItem
          to="/settings"
          icon={Gear}
          label="Inställningar"
          testId="nav-settings"
          onClick={onNavigate}
          showDot={isAdmin && changelogUnread > 0}
        />
        <button
          type="button"
          data-testid="nav-help-button"
          onClick={() => {
            setHelpOpen(true);
            if (onNavigate) onNavigate();
          }}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-sidebar-text hover:bg-sidebar-active/60 transition-colors"
        >
          <Question size={20} weight="regular" />
          <span className="flex-1 text-left">Hjälp & feedback</span>
        </button>
        {isSuperAdmin && (
          <NavItem
            to="/admin/inquiries"
            icon={Envelope}
            label="Leads"
            testId="nav-admin-inquiries"
            onClick={onNavigate}
          />
        )}
      </nav>

      <div className="pt-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 py-2">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-9 h-9 rounded-full object-cover"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-sidebar-active flex items-center justify-center text-white">
              {user?.name?.[0] || "?"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-[11px] uppercase tracking-wider text-sidebar-text/70">
              {user?.role === "admin" ? "Admin" : "Anställd"}
            </div>
          </div>
        </div>
        <button
          data-testid="logout-button"
          onClick={logout}
          className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-sidebar-active/60 text-sidebar-text"
        >
          <SignOut size={18} /> Logga ut
        </button>
      </div>
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}

export default function AppShell({ children }) {
  const { companyName, logoUrl } = useBranding();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Stäng mobilmenyn automatiskt när route ändras (t.ex. via bakåtknapp)
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lås bakgrundsskroll när mobilmenyn är öppen
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-app flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 bg-sidebar text-sidebar-text px-5 pt-5 pb-3 fixed h-screen h-[100dvh]">
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-sidebar text-sidebar-text px-4 py-3 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          {logoUrl ? (
            <div className="w-8 h-8 rounded-md bg-white/95 flex items-center justify-center overflow-hidden p-1 flex-shrink-0">
              <img
                src={logoUrl}
                alt={companyName}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-8 h-8 rounded-md bg-brand flex items-center justify-center font-heading text-white text-sm flex-shrink-0">
              {(companyName[0] || "T").toUpperCase()}
            </div>
          )}
          <span className="font-heading text-base text-white truncate">Tidsrapporten</span>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(true)}
          aria-label="Öppna meny"
          className="p-2 -mr-2 rounded-lg hover:bg-sidebar-active/60 text-white"
        >
          <List size={24} weight="bold" />
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            data-testid="mobile-menu-backdrop"
            className="md:hidden fixed inset-0 z-50 bg-black/60"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            data-testid="mobile-menu-drawer"
            className="md:hidden fixed top-0 left-0 z-50 h-screen w-72 max-w-[85vw] bg-sidebar text-sidebar-text p-5 flex flex-col animate-slide-in-left"
          >
            <button
              data-testid="mobile-menu-close"
              onClick={() => setMobileOpen(false)}
              aria-label="Stäng meny"
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-sidebar-active/60 text-white"
            >
              <X size={20} weight="bold" />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      <main className="flex-1 md:ml-64 pt-14 md:pt-0">
        <TrialBanner />
        <div className="max-w-7xl mx-auto p-5 md:p-10">{children}</div>
      </main>
    </div>
  );
}
